/** Shared types and helpers for Leasing Season Config Setup tabs. */

/** Setup + Compose nav strip, bundle bar, and selected artifact column. */
export const LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH = 1200

export type SetupArtifactKind =
    | 'applicant_data'
    | 'guest_card'
    | 'application_fee'
    | 'conditional_approvals'
    | 'signable_docs'
    | 'property_data'

export type SetupConfigSource = 'existing' | 'ezos' | 'org' | 'scratch'

export interface SetupConfigOption {
    id: string
    name: string
    /**
     * Lane blurb for EZOS/org template cards (not used as Selected provenance).
     * For property-owned (Edit existing), prefer summary + modifiedAt.
     */
    description: string
    /** ISO timestamp — shown on chooser cards as “Modified …”. */
    modifiedAt?: string
    /** Template/org name this property copy was adopted from. */
    adoptedFromName?: string
    adoptedFromSource?: 'ezos' | 'org' | 'scratch'
    /** Org applied + locked — property cannot change or edit. */
    orgMandated?: boolean
}

export interface SelectedSetupConfig {
    id: string
    name: string
    source: SetupConfigSource
    /** Org applied + locked — property cannot change or edit. */
    orgMandated?: boolean
    /** @deprecated Prefer origin icon line via adoptedFromSource/Name. */
    provenance?: string
    modifiedAt?: string
    adoptedFromName?: string
    adoptedFromSource?: 'ezos' | 'org' | 'scratch'
    /**
     * Other Leasing Season Configs pinning this artifact.
     * When > 0, the tab is read-only until the PM copies it for this config.
     */
    usedByOtherConfigs?: number
}

export interface SetupMemberSlot {
    id: string
    label: string
    description: string
    required: boolean
    /** Soft flag for UI — slot is shown but not assignable yet. */
    comingSoon?: boolean
}

export const ARTIFACT_LABELS: Record<SetupArtifactKind, string> = {
    applicant_data: 'Applicant Data',
    guest_card: 'Guest Card',
    application_fee: 'Application Fee',
    conditional_approvals: 'Qualifying Methods',
    signable_docs: 'Signable Documents',
    property_data: 'Property Data',
}

/** Guest card pack — channel slots. */
export const GUEST_CARD_SLOTS: SetupMemberSlot[] = [
    {
        id: 'walk_in',
        label: 'Walk-in',
        description:
            'Front desk / QR intake — the guest card used for this channel.',
        required: true,
    },
    {
        id: 'campaign',
        label: 'Campaign',
        description:
            'Default for outbound campaigns. New campaign messages start with this card; you can change it per message.',
        required: true,
    },
    {
        id: 'website',
        label: 'Website',
        description:
            'Default for website embeds and shareable links. Floor-plan links can still use this or another card in Web Tools.',
        required: true,
    },
]

/**
 * @deprecated Signable Documents is lease + guarantor packets, not purpose slots.
 */
export const SIGNABLE_DOC_SLOTS: SetupMemberSlot[] = [
    {
        id: 'lease_contract',
        label: 'Lease contract',
        description: 'Core lease document in the lease packet',
        required: true,
    },
    {
        id: 'guarantor_agreement',
        label: 'Guarantor agreement',
        description: 'Personal guarantor path (assumed in student housing)',
        required: true,
    },
    {
        id: 'leap_addendum',
        label: 'Leap / GWP addendum',
        description: 'Used when Leap is offered as a conditional package',
        required: false,
    },
    {
        id: 'enhanced_deposit_ack',
        label: 'Enhanced deposit acknowledgment',
        description: 'Optional acknowledgment for an enhanced deposit package',
        required: false,
    },
    {
        id: 'rent_prepay_ack',
        label: 'Rent prepayment acknowledgment',
        description: 'Optional acknowledgment for a rent prepayment package',
        required: false,
    },
]

const DAYS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
]

/** Short label for chooser cards, e.g. "Modified Jul 30, 2026". */
export function formatModifiedLabel(iso?: string | null): string | null {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return `Modified ${DAYS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
}

export function nowIso(): string {
    return new Date().toISOString()
}

/** Lane blurb only (no modified) for EZOS / org template cards. */
export function formatTemplateOptionSummary(option: SetupConfigOption): string {
    return option.description || ''
}

export type PropertyConfigCatalog = Record<
    SetupArtifactKind,
    SetupConfigOption[]
>

/** Empty catalog — filled from property APIs only (no fake starter rows). */
export function createInitialPropertyCatalog(): PropertyConfigCatalog {
    return {
        applicant_data: [],
        guest_card: [],
        application_fee: [],
        conditional_approvals: [],
        signable_docs: [],
        property_data: [],
    }
}

/** EZOS setup templates — not wired yet; keep lanes empty until a real API exists. */
export function getEzosSetupTemplates(
    _kind: SetupArtifactKind,
): SetupConfigOption[] {
    return []
}

/** Org setup templates — not wired yet; keep lanes empty until a real API exists. */
export function getOrgSetupTemplates(
    _kind: SetupArtifactKind,
): SetupConfigOption[] {
    return []
}

export function scratchDefaultName(kind: SetupArtifactKind): string {
    switch (kind) {
        case 'applicant_data':
            return 'Custom workbook'
        case 'guest_card':
            return 'Custom guest card pack'
        case 'application_fee':
            return 'Custom fee policy'
        case 'conditional_approvals':
            return 'Custom qualifying methods'
        case 'signable_docs':
            return 'Custom signable doc set'
        case 'property_data':
            return 'Custom property data'
        default:
            return 'Custom configuration'
    }
}

export function createSelectedFromOption(
    option: SetupConfigOption,
    source: SetupConfigSource,
    extras?: { orgMandated?: boolean },
): SelectedSetupConfig {
    return {
        id: option.id,
        name: option.name,
        source,
        orgMandated: extras?.orgMandated,
        modifiedAt: option.modifiedAt,
        adoptedFromName: option.adoptedFromName,
        adoptedFromSource: option.adoptedFromSource,
    }
}

export function createScratchSelected(
    kind: SetupArtifactKind,
    name: string,
): SelectedSetupConfig {
    const modifiedAt = nowIso()
    return {
        id: `scratch-${kind}-${Date.now()}`,
        name,
        source: 'scratch',
        modifiedAt,
        adoptedFromSource: 'scratch',
    }
}

/**
 * Adopt EZOS/org/scratch into a new property-owned config.
 * Returns the selected config and the updated Edit-existing list.
 */
export function adoptIntoPropertyCatalog(
    existing: SetupConfigOption[],
    args: {
        name: string
        source: 'ezos' | 'org' | 'scratch'
        template?: SetupConfigOption
        kind: SetupArtifactKind
    },
): { selected: SelectedSetupConfig; nextExisting: SetupConfigOption[] } {
    const modifiedAt = nowIso()
    const templateName = args.template?.name
    const id =
        args.source === 'scratch'
            ? `prop-${args.kind}-${Date.now()}`
            : `prop-${args.kind}-${args.template?.id ?? 'x'}-${Date.now()}`

    const option: SetupConfigOption = {
        id,
        name: args.name,
        description: 'Owned by this property',
        modifiedAt,
        adoptedFromName: args.source === 'scratch' ? undefined : templateName,
        adoptedFromSource: args.source,
    }

    const selected: SelectedSetupConfig = {
        id,
        name: args.name,
        // After adopt, the binding is a property-owned config.
        source: 'existing',
        modifiedAt,
        adoptedFromName: option.adoptedFromName,
        adoptedFromSource: option.adoptedFromSource,
    }

    // Newest first in Edit existing.
    const nextExisting = [option, ...existing.filter((e) => e.id !== id)]
    return { selected, nextExisting }
}

/** Bump modifiedAt when reopening an existing config for edit. */
export function touchExistingOption(
    existing: SetupConfigOption[],
    optionId: string,
): {
    option: SetupConfigOption | null
    nextExisting: SetupConfigOption[]
} {
    const modifiedAt = nowIso()
    let touched: SetupConfigOption | null = null
    const nextExisting = existing.map((item) => {
        if (item.id !== optionId) return item
        touched = { ...item, modifiedAt }
        return touched
    })
    return { option: touched, nextExisting }
}

export type MemberConfiguredMap = Record<string, boolean>

export function emptyMemberMap(slots: SetupMemberSlot[]): MemberConfiguredMap {
    return Object.fromEntries(slots.map((s) => [s.id, false]))
}

export function requiredSlotsReady(
    slots: SetupMemberSlot[],
    configured: MemberConfiguredMap,
): boolean {
    return slots
        .filter((s) => s.required && !s.comingSoon)
        .every((s) => configured[s.id] === true)
}

export function countConfigured(
    slots: SetupMemberSlot[],
    configured: MemberConfiguredMap,
): { configured: number; required: number; requiredReady: number } {
    const required = slots.filter((s) => s.required && !s.comingSoon)
    return {
        configured: slots.filter((s) => configured[s.id]).length,
        required: required.length,
        requiredReady: required.filter((s) => configured[s.id]).length,
    }
}
